import React, { useContext, useMemo, useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import "./QuotesPanel.scss";
import { I18nContext } from "../../../../../../context/i18n.context";
import {
  PersonProjectI,
  PersonProjectStateT,
} from "../../../../../../api/request/Lead/Interfaces/PersonProjectInterface";
import {
  CapitalChangePreviewI,
  CapitalChangeResultI,
} from "../../../../../../api/request/Lead/Interfaces/CapitalChangeInterface";
import CapitalChangeDialog from "./CapitalChangeDialog";
import { isQuoteStagePrice, isQuoteStageResult } from "../../../../../../native/capitalChange";
import {
  ArrangedQuoteI,
  arrangeQuotes,
  FunnelStepT,
  isContinuable,
} from "./quotesOrder";

// Color del mercado: App.js inyecta --primary-color (IT #003399, AXA WL #1943A0);
// fallback azul Life5.
const BRAND = "var(--primary-color, #423EF4)";
const BRAND_SOFT = "var(--light-primary-color, #EEF0FF)";

interface QuotesPanelI {
  projects: PersonProjectI[];
  currentLeadId: string;
  /** Retomar la cotización elegida (la de la sesión o cambiando de sesión). */
  onContinue: (project: PersonProjectI) => Promise<void> | void;
  onNewQuote: () => void;
  /**
   * «Archivia preventivo» (30-oct-2026): solo en los que el gateway marca
   * `archivable` (nada emitido ni cobrado). Sin él, no se ofrece.
   */
  onArchive?: (project: PersonProjectI) => Promise<void> | void;
  /**
   * «Modifica capitale» (04-nov-2026): solo en los que el gateway marca
   * `capitalChangeable` (hasta la nota legal). Hacen falta las dos: la vista
   * previa (no graba) y el cambio. Sin ellas, no se ofrece.
   */
  onPreviewCapital?: (
    project: PersonProjectI,
    capital?: number,
  ) => Promise<CapitalChangePreviewI>;
  onChangeCapital?: (
    project: PersonProjectI,
    capital: number,
    operationId: string,
  ) => Promise<CapitalChangeResultI>;
  /** Inyectable para los tests ("Aggiornato oggi / 3 giorni fa"). */
  now?: Date;
}

const CHIP: Record<PersonProjectStateT, { bg: string; fg: string }> = {
  IN_PROGRESS: { bg: "#dcfce7", fg: "#166534" },
  DRAFT: { bg: "#ffedd5", fg: "#9a3412" },
  EXPIRED: { bg: "#f3f4f6", fg: "#6b7280" },
  POLICY: { bg: "#e0e7ff", fg: "#3730a3" },
};

const daysBetween = (from: Date, to: Date): number => {
  const day = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.max(0, Math.round((day(to) - day(from)) / 86_400_000));
};

const QuotesPanel = ({
  projects,
  currentLeadId,
  onContinue,
  onNewQuote,
  onArchive,
  onPreviewCapital,
  onChangeCapital,
  now,
}: QuotesPanelI) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  // Las claves viven en getlife/translations; hasta que lleguen, fallback IT.
  const t = (key: string, fallback: string): string => {
    const value = translate(key);
    return value === key ? fallback : value;
  };

  // Los que el cliente acaba de archivar salen ya, sin esperar a recargar la lista.
  const [archived, setArchived] = useState<ReadonlySet<string>>(new Set());
  const [archiving, setArchiving] = useState<PersonProjectI | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveFailed, setArchiveFailed] = useState(false);
  const [archivedNotice, setArchivedNotice] = useState(false);
  const [changingCapital, setChangingCapital] = useState<PersonProjectI | null>(
    null,
  );
  const [capitalNotice, setCapitalNotice] = useState<string | null>(null);
  const visible = useMemo(
    () => projects.filter((p) => !archived.has(p.leadUlid)),
    [projects, archived],
  );
  const { quotes, lead } = useMemo(
    () => arrangeQuotes(visible, currentLeadId),
    [visible, currentLeadId],
  );
  // Preselección: el alta en curso (la abierta más avanzada).
  const [selected, setSelected] = useState<string | null>(
    () => lead?.project.leadUlid ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const selectedQuote =
    quotes.find((q) => q.project.leadUlid === selected) ?? null;
  const selectedProject = selectedQuote?.project ?? null;
  const canContinue =
    null !== selectedProject && isContinuable(selectedProject) && !busy;
  // Un alta más atrás que la en curso no lleva el «Continua» principal: se
  // retoma con confirmación.
  const isBehindLead =
    null !== selectedQuote &&
    ("PREVIOUS" === selectedQuote.role || "BEHIND" === selectedQuote.role);

  const money = (amount: number, decimals: number) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);

  const stateLabel = (state: PersonProjectStateT): string =>
    ({
      IN_PROGRESS: t("newDashboard.quotes.state.inProgress", "In corso"),
      DRAFT: t("newDashboard.quotes.state.draft", "Bozza"),
      EXPIRED: t("newDashboard.quotes.state.expired", "Scaduto"),
      POLICY: t("newDashboard.quotes.state.policy", "Polizza"),
    })[state];

  const stepLabel = (step: FunnelStepT): string =>
    ({
      initialQuestions: t(
        "newDashboard.quotes.step.initialQuestions",
        "Domande iniziali",
      ),
      medical: t("newDashboard.quotes.step.medical", "Questionario medico"),
      advisor: t("newDashboard.quotes.step.advisor", "Attesa del consulente"),
      offer: t("newDashboard.quotes.step.offer", "Scelta dell'offerta"),
      payment: t("newDashboard.quotes.step.payment", "Pagamento"),
      personalData: t(
        "newDashboard.quotes.step.personalData",
        "Dati personali",
      ),
      address: t("newDashboard.quotes.step.address", "Indirizzo"),
      beneficiaries: t("newDashboard.quotes.step.beneficiaries", "Beneficiari"),
      idCard: t("newDashboard.quotes.step.idCard", "Documento d'identità"),
      consents: t("newDashboard.quotes.step.consents", "Consensi"),
      legalNote: t("newDashboard.quotes.step.legalNote", "Nota legale"),
      signature: t("newDashboard.quotes.step.signature", "Firma"),
    })[step];

  /** «In corso · Nota legale» para el alta en curso; «Preventivo precedente» para las de atrás. */
  const chipFor = (
    q: ArrangedQuoteI,
  ): { label: string; bg: string; fg: string } => {
    if ("LEAD" === q.role) {
      const inProgress = stateLabel("IN_PROGRESS");
      return {
        label:
          null === q.step ? inProgress : `${inProgress} · ${stepLabel(q.step)}`,
        ...CHIP.IN_PROGRESS,
      };
    }
    if ("PREVIOUS" === q.role) {
      return {
        label: t("newDashboard.quotes.state.previous", "Preventivo precedente"),
        ...CHIP.EXPIRED,
      };
    }
    if ("BEHIND" === q.role && null !== q.step) {
      return { label: stepLabel(q.step), ...CHIP.EXPIRED };
    }
    return { label: stateLabel(q.project.state), ...CHIP[q.project.state] };
  };

  const coverageLabel = (code: string): string =>
    ({
      DEATH: t("newDashboard.quotes.coverage.death", "Decesso"),
      MALATTIA_TERMINALE: t(
        "newDashboard.quotes.coverage.terminalIllness",
        "Malattia terminale",
      ),
      DISABILITY: t("newDashboard.quotes.coverage.disability", "Invalidità"),
    })[code] ?? code;

  const detailLine = (p: PersonProjectI): string => {
    const parts: string[] = [];
    if (null !== p.capital && 0 < p.capital) {
      parts.push(
        `${t("newDashboard.quotes.capital", "Capitale")} ${money(p.capital, 0)}`,
      );
    }
    const coverages = (0 < p.coverages.length ? p.coverages : ["DEATH"]).map(
      coverageLabel,
    );
    parts.push(coverages.join(" + "));
    return parts.join(" · ");
  };

  const updatedLabel = (iso: string): string => {
    const days = daysBetween(new Date(iso), now ?? new Date());
    if (0 === days)
      return t("newDashboard.quotes.updatedToday", "Aggiornato oggi");
    if (1 === days)
      return t("newDashboard.quotes.updatedYesterday", "Aggiornato ieri");
    return t(
      "newDashboard.quotes.updatedDaysAgo",
      "Aggiornato {{days}} giorni fa",
    ).replace("{{days}}", String(days));
  };

  const priceLabel = (p: PersonProjectI): string | null => {
    const promo =
      null !== p.promotedPrice && 0 < p.promotedPrice ? p.promotedPrice : null;
    const amount = promo ?? p.price;
    if (null === amount || 0 >= amount) return null;
    const suffix =
      "yearly" === p.period
        ? t("newDashboard.quotes.perYear", "/anno")
        : t("newDashboard.quotes.perMonth", "/mese");
    return `${money(amount, 2)}${suffix}`;
  };

  const resume = async (project: PersonProjectI) => {
    setBusy(true);
    setFailed(false);
    try {
      await onContinue(project);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // Varias altas por persona son el multiquote: la confirmación solo informa de
  // cuál va más avanzada y deja elegir cualquiera de las dos.
  const continueSelected = () => {
    if (!canContinue || null === selectedProject) return;
    if (isBehindLead) {
      setConfirming(true);
      return;
    }
    void resume(selectedProject);
  };

  const confirmSelected = () => {
    setConfirming(false);
    if (null !== selectedProject) void resume(selectedProject);
  };

  const confirmLead = () => {
    setConfirming(false);
    if (null === lead) return;
    setSelected(lead.project.leadUlid);
    void resume(lead.project);
  };

  const confirmArchive = async () => {
    if (null === archiving || undefined === onArchive) return;
    setArchiveBusy(true);
    setArchiveFailed(false);
    try {
      await onArchive(archiving);
      setArchived((prev) => new Set(prev).add(archiving.leadUlid));
      if (selected === archiving.leadUlid) setSelected(null);
      setArchiving(null);
      setArchivedNotice(true);
    } catch {
      setArchiveFailed(true);
    } finally {
      setArchiveBusy(false);
    }
  };

  const closeArchive = () => {
    if (archiveBusy) return;
    setArchiving(null);
    setArchiveFailed(false);
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "#6b7280",
    margin: "0 0 12px",
    textTransform: "uppercase",
  };

  const primaryButton: React.CSSProperties = {
    width: "100%",
    padding: "16px 20px",
    borderRadius: 12,
    border: "none",
    backgroundColor: BRAND,
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    boxShadow: "0 4px 12px rgba(17,24,39,0.15)",
  };

  const secondaryButton: React.CSSProperties = {
    ...primaryButton,
    padding: "15px 20px",
    border: `1px solid ${BRAND}`,
    backgroundColor: "#fff",
    color: BRAND,
    boxShadow: "none",
  };

  const iconTile = (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: BRAND_SOFT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND,
      }}
    >
      {/* El icono pinta `fill` como atributo SVG, donde var() no resuelve: currentColor. */}
      <GlobalIcon iconName="ShieldIcon" size="XS" color="currentColor" />
    </div>
  );

  return (
    <section
      data-testid="quotes-panel"
      style={{ display: "flex", flexDirection: "column", gap: 24 }}
    >
      <header>
        <h2
          style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}
        >
          {t("newDashboard.quotes.title", "I tuoi preventivi")}
        </h2>
        <p style={{ fontSize: 15, color: "#6b7280", margin: "6px 0 0" }}>
          {t(
            "newDashboard.quotes.subtitle",
            "Riprendine uno che hai già o creane uno nuovo. Puoi averne più di uno alla volta.",
          )}
        </p>
      </header>

      {/* Desktop: lista a la izquierda y resumen del elegido + CTA a la derecha
          (fijo al hacer scroll). Móvil: una columna y CTA fijo abajo. */}
      <div className="quotesPanel__layout">
        <div className="quotesPanel__main">
          {0 < projects.length && (
            <div>
              <p style={sectionLabel}>
                {t("newDashboard.quotes.saved", "Salvati")}
              </p>
              <div
                role="radiogroup"
                aria-label={t("newDashboard.quotes.saved", "Salvati")}
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {quotes.map((q) => {
                  const p = q.project;
                  const isSelected = p.leadUlid === selected;
                  const enabled = isContinuable(p);
                  const chip = chipFor(q);
                  const price = priceLabel(p);
                  const canArchive =
                    undefined !== onArchive && true === p.archivable;
                  const canChangeCapital =
                    undefined !== onPreviewCapital &&
                    undefined !== onChangeCapital &&
                    true === p.capitalChangeable;
                  return (
                    <div key={p.leadUlid} className="quotesPanel__quote">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={!enabled}
                      data-testid={`quote-${p.leadUlid}`}
                      onClick={() => enabled && setSelected(p.leadUlid)}
                      style={{
                        textAlign: "left",
                        width: "100%",
                        padding: 16,
                        borderRadius: 16,
                        border: `${isSelected ? 2 : 1}px solid ${isSelected ? BRAND : "#e5e7eb"}`,
                        backgroundColor: isSelected ? BRAND_SOFT : "#fff",
                        cursor: enabled ? "pointer" : "default",
                        opacity: enabled ? 1 : 0.7,
                        font: "inherit",
                      }}
                    >
                      {/* El chip baja de línea cuando no cabe al lado (móvil). */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        {iconTile}
                        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: "#111827",
                            }}
                          >
                            {t(
                              "newDashboard.quotes.product.life",
                              "Assicurazione vita",
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              color: "#6b7280",
                              marginTop: 2,
                            }}
                          >
                            {detailLine(p)}
                          </div>
                          {"PREVIOUS" === q.role && null !== q.step && (
                            <div
                              style={{
                                fontSize: 13,
                                color: "#6b7280",
                                marginTop: 2,
                              }}
                            >
                              {t(
                                "newDashboard.quotes.atStep",
                                "Al passo: {{step}}",
                              ).replace("{{step}}", stepLabel(q.step))}
                            </div>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 999,
                            backgroundColor: chip.bg,
                            color: chip.fg,
                            whiteSpace: "nowrap",
                            alignSelf: "flex-start",
                            marginLeft: "auto",
                          }}
                        >
                          {chip.label}
                        </span>
                      </div>
                      <div
                        style={{
                          borderTop: "1px solid #e5e7eb",
                          marginTop: 14,
                          paddingTop: 12,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: 14,
                        }}
                      >
                        <span style={{ color: "#6b7280" }}>
                          {updatedLabel(p.updatedAt)}
                        </span>
                        {null !== price && (
                          <span style={{ fontWeight: 700, color: "#111827" }}>
                            {price}
                          </span>
                        )}
                      </div>
                    </button>
                    {(canChangeCapital || canArchive) && (
                      <div className="quotesPanel__actions">
                        {canChangeCapital && (
                          <button
                            type="button"
                            className="quotesPanel__archive"
                            data-testid={`quote-capital-${p.leadUlid}`}
                            aria-label={`${t("newDashboard.quotes.capitalChange.action", "Modifica capitale")}: ${detailLine(p)}`}
                            onClick={() => {
                              setCapitalNotice(null);
                              setArchivedNotice(false);
                              setChangingCapital(p);
                            }}
                          >
                            {t(
                              "newDashboard.quotes.capitalChange.action",
                              "Modifica capitale",
                            )}
                          </button>
                        )}
                        {canArchive && (
                          <button
                            type="button"
                            className="quotesPanel__archive"
                            data-testid={`quote-archive-${p.leadUlid}`}
                            aria-label={`${t("newDashboard.quotes.archive.action", "Archivia preventivo")}: ${detailLine(p)}`}
                            onClick={() => {
                              setArchivedNotice(false);
                              setCapitalNotice(null);
                              setArchiving(p);
                            }}
                          >
                            {t("newDashboard.quotes.archive.action", "Archivia preventivo")}
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {archivedNotice && (
            <p role="status" className="quotesPanel__archived">
              {t("newDashboard.quotes.archive.done", "Preventivo archiviato.")}
            </p>
          )}

          {null !== capitalNotice && (
            <p role="status" className="quotesPanel__archived">
              {capitalNotice}
            </p>
          )}

          <div>
            <p style={sectionLabel}>
              {t("newDashboard.quotes.createNew", "Creane uno nuovo")}
            </p>
            <button
              type="button"
              onClick={onNewQuote}
              data-testid="quotes-new"
              style={{
                textAlign: "left",
                width: "100%",
                padding: 16,
                borderRadius: 16,
                border: "1px solid #e5e7eb",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
                font: "inherit",
              }}
            >
              {iconTile}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}
                >
                  {t("newDashboard.quotes.product.life", "Assicurazione vita")}
                </div>
                <div style={{ fontSize: 14, color: "#6b7280", marginTop: 2 }}>
                  {t(
                    "newDashboard.quotes.product.lifeClaim",
                    "Proteggi i tuoi cari con un capitale su misura.",
                  )}
                </div>
              </div>
              <span style={{ color: BRAND, display: "flex" }}>
                <GlobalIcon
                  iconName="PlusIcon"
                  size="XS"
                  color="currentColor"
                />
              </span>
            </button>
          </div>
        </div>

        {null !== selected && (
          <aside className="quotesPanel__aside" data-testid="quotes-summary">
            {null !== selectedProject && (
              <div className="quotesPanel__summary">
                <p style={sectionLabel}>
                  {t(
                    "newDashboard.quotes.summary.title",
                    "Il preventivo scelto",
                  )}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {iconTile}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {t(
                        "newDashboard.quotes.product.life",
                        "Assicurazione vita",
                      )}
                    </div>
                    <div
                      style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}
                    >
                      {updatedLabel(selectedProject.updatedAt)}
                    </div>
                  </div>
                </div>
                <dl className="quotesPanel__facts">
                  <div>
                    <dt>{t("newDashboard.quotes.capital", "Capitale")}</dt>
                    <dd>
                      {null !== selectedProject.capital &&
                      0 < selectedProject.capital
                        ? money(selectedProject.capital, 0)
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {t("newDashboard.quotes.summary.coverages", "Coperture")}
                    </dt>
                    <dd>
                      {(0 < selectedProject.coverages.length
                        ? selectedProject.coverages
                        : ["DEATH"]
                      )
                        .map(coverageLabel)
                        .join(", ")}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("newDashboard.quotes.summary.state", "Stato")}</dt>
                    <dd>
                      {null !== selectedQuote && chipFor(selectedQuote).label}
                    </dd>
                  </div>
                </dl>
                {null !== priceLabel(selectedProject) && (
                  <div className="quotesPanel__price">
                    {priceLabel(selectedProject)}
                  </div>
                )}
              </div>
            )}
            {failed && (
              <p
                role="alert"
                style={{ fontSize: 14, color: "#b91c1c", margin: "0 0 8px" }}
              >
                {t(
                  "newDashboard.quotes.continueError",
                  "Non siamo riusciti ad aprire questo preventivo. Riprova tra qualche istante.",
                )}
              </p>
            )}
            <button
              type="button"
              onClick={continueSelected}
              disabled={!canContinue}
              style={{
                ...(isBehindLead ? secondaryButton : primaryButton),
                opacity: canContinue ? 1 : 0.4,
                cursor: canContinue ? "pointer" : "default",
              }}
            >
              {isBehindLead
                ? t("newDashboard.quotes.resume", "Riprendi questo preventivo")
                : t(
                    "newDashboard.quotes.continue",
                    "Continua con questo preventivo",
                  )}
            </button>
          </aside>
        )}
      </div>

      {confirming && null !== lead && null !== lead.step && (
        <div
          className="quotesPanel__backdrop"
          onClick={() => setConfirming(false)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotes-confirm-text"
            data-testid="quotes-confirm"
            className="quotesPanel__dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => "Escape" === e.key && setConfirming(false)}
          >
            <p
              id="quotes-confirm-text"
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: "#111827",
                margin: 0,
              }}
            >
              {t(
                "newDashboard.quotes.confirm.text",
                "Stai per riprendere un altro preventivo, il più avanzato è al passo «{{step}}».",
              ).replace("{{step}}", stepLabel(lead.step))}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                type="button"
                autoFocus
                onClick={confirmSelected}
                style={{ ...primaryButton, cursor: "pointer" }}
              >
                {t(
                  "newDashboard.quotes.confirm.selected",
                  "Continua con questo preventivo",
                )}
              </button>
              <button
                type="button"
                onClick={confirmLead}
                style={{ ...secondaryButton, cursor: "pointer" }}
              >
                {t(
                  "newDashboard.quotes.confirm.lead",
                  "Continua con il più avanzato",
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {null !== archiving && (
        <div className="quotesPanel__backdrop" onClick={closeArchive}>
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotes-archive-title"
            aria-describedby="quotes-archive-text"
            data-testid="quotes-archive-confirm"
            className="quotesPanel__dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => "Escape" === e.key && closeArchive()}
          >
            <p
              id="quotes-archive-title"
              style={{ fontSize: 17, fontWeight: 600, color: "#111827", margin: 0 }}
            >
              {t("newDashboard.quotes.archive.title", "Archiviare questo preventivo?")}
            </p>
            <p
              id="quotes-archive-text"
              style={{ fontSize: 15, color: "#4b5563", margin: 0 }}
            >
              {t(
                "newDashboard.quotes.archive.text",
                "{{quote}}. Non comparirà più tra i tuoi preventivi. Se cambi idea, il nostro servizio clienti può recuperarlo.",
              ).replace("{{quote}}", detailLine(archiving))}
            </p>
            {archiveFailed && (
              <p role="alert" style={{ fontSize: 14, color: "#b91c1c", margin: 0 }}>
                {t(
                  "newDashboard.quotes.archive.error",
                  "Non siamo riusciti ad archiviare il preventivo. Riprova tra qualche istante.",
                )}
              </p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                type="button"
                autoFocus
                onClick={closeArchive}
                disabled={archiveBusy}
                style={{ ...primaryButton, cursor: "pointer" }}
              >
                {t("newDashboard.quotes.archive.cancel", "Annulla")}
              </button>
              <button
                type="button"
                onClick={() => void confirmArchive()}
                disabled={archiveBusy}
                aria-busy={archiveBusy}
                style={{
                  ...secondaryButton,
                  cursor: archiveBusy ? "default" : "pointer",
                  opacity: archiveBusy ? 0.6 : 1,
                }}
              >
                {t("newDashboard.quotes.archive.confirm", "Archivia")}
              </button>
            </div>
          </div>
        </div>
      )}
      {null !== changingCapital &&
        undefined !== onPreviewCapital &&
        undefined !== onChangeCapital && (
          <CapitalChangeDialog
            project={changingCapital}
            detail={detailLine(changingCapital)}
            onPreview={onPreviewCapital}
            onConfirm={onChangeCapital}
            onClose={() => setChangingCapital(null)}
            onChanged={(result) => {
              setChangingCapital(null);
              // Sin oferta elegida (v1.1): no hay prima vigente que anunciar; el
              // cliente elige periodicidad y decorrenza al continuar.
              if (isQuoteStageResult(result)) {
                setCapitalNotice(
                  t(
                    "newDashboard.quotes.capitalChange.doneQuote",
                    "Capitale aggiornato a {{capital}}. Continua il preventivo per scegliere periodicità e decorrenza.",
                  ).replace("{{capital}}", money(result.to.capital, 0)),
                );
                return;
              }
              const done = t(
                "newDashboard.quotes.capitalChange.done",
                "Capitale aggiornato a {{capital}}.",
              ).replace("{{capital}}", money(result.to.capital, 0));
              const premium =
                null === result.current || isQuoteStagePrice(result.current)
                  ? ""
                  : ` ${t(
                      "newDashboard.quotes.capitalChange.donePremium",
                      "Nuovo premio: {{premium}} {{period}}.",
                    )
                      .replace("{{premium}}", money(result.current.firstPayment, 2))
                      .replace(
                        "{{period}}",
                        "YEARLY" === result.current.frequency
                          ? t("newDashboard.quotes.capitalChange.perYear", "all'anno")
                          : t("newDashboard.quotes.capitalChange.perMonth", "al mese"),
                      )}`;
              setCapitalNotice(`${done}${premium}`);
            }}
            t={t}
          />
        )}
    </section>
  );
};

export default QuotesPanel;
